import {
  resolveAppointmentStatus,
  toPatientFacingAppointmentStatus,
  type PatientFacingAppointmentStatus,
} from '@/utils/appointmentStatus';

export type AppointmentLifecycleStatus = 'upcoming' | 'completed' | 'cancelled' | 'no_show';

/**
 * Staff may reschedule upcoming or completed slots (including payment-completed).
 * Payment status must not lock the appointment. Cancelled and no-show stay blocked.
 */
export function canStaffRescheduleAppointment(status: AppointmentLifecycleStatus): boolean {
  switch (status) {
    case 'upcoming':
    case 'completed':
      return true;
    case 'cancelled':
    case 'no_show':
      return false;
    default: {
      const _exhaustive: never = status;
      throw new Error(`Unhandled appointment status: ${_exhaustive}`);
    }
  }
}

/**
 * Staff may open Edit / save a reschedule.
 * Completed (often paid-in-advance) stays editable. Overdue upcoming stays locked.
 * Cancelled and no-show stay locked.
 */
export function isAppointmentEditable(
  status: AppointmentLifecycleStatus,
  startsAt: string | Date,
): boolean {
  if (!canStaffRescheduleAppointment(status)) return false;
  if (status === 'completed') return true;
  return new Date(startsAt).getTime() >= Date.now();
}

/**
 * Start-time check kept for editability tests.
 * Patients → Aftaler uses `appointmentIsListedUpcoming` (slot end,
 * Europe/Copenhagen) so chips, badges, and Tidligere stay aligned.
 */
export function isUpcomingOnCalendar(
  status: AppointmentLifecycleStatus,
  startsAt: string | Date,
  now: Date = new Date(),
): boolean {
  if (status === 'cancelled' || status === 'no_show') return false;
  return new Date(startsAt).getTime() >= now.getTime();
}

/**
 * Patient-facing appointment lifecycle. Delegates to the shared resolver so
 * calendar, details, Aftaler, and the portal never diverge.
 */
export type { PatientFacingAppointmentStatus };

export function resolvePatientFacingAppointmentStatus(input: {
  status: AppointmentLifecycleStatus;
  visitStatus?: string | null;
  startsAt: Date | string;
  endsAt?: Date | string;
  now?: Date;
}): PatientFacingAppointmentStatus {
  return toPatientFacingAppointmentStatus(
    resolveAppointmentStatus({
      status: input.status,
      visitStatus: input.visitStatus,
      startsAt: input.startsAt,
      endsAt: input.endsAt,
      now: input.now,
    }),
  );
}

/**
 * Patient Portal Kommende / Tidligere grouping.
 *
 * Consumes the patient-facing appointment status, never payment / invoice /
 * klippekort / `appointments.status = completed`. Paid-in-advance visits
 * resolve to `upcoming` while the slot is still live. Cancelled and no-show
 * stay out of Kommende even if the wall-clock time is in the future.
 */
export function isPortalUpcomingAppointment(input: {
  displayStatus: PatientFacingAppointmentStatus;
  startsAt: Date | string;
  endsAt?: Date | string;
  now?: Date;
}): boolean {
  switch (input.displayStatus) {
    case 'cancelled':
    case 'no_show':
    case 'completed':
      return false;
    case 'checked_in':
    case 'in_treatment':
      return true;
    case 'upcoming':
      break;
    default: {
      const _exhaustive: never = input.displayStatus;
      throw new Error(`Unhandled patient-facing status: ${_exhaustive}`);
    }
  }

  const now = input.now ?? new Date();
  const end = input.endsAt ?? input.startsAt;
  return new Date(end).getTime() >= now.getTime();
}
